import { useState } from 'react';
import { motion } from 'framer-motion';
import { Layers, RefreshCw, Loader2, Minimize2 } from 'lucide-react';
import { CardioMedia } from '@/components/CardioMedia';
import { getCardioImage } from '@/utils/cardioImages';

const workLabel = (s) => (s % 60 === 0 ? `${s / 60}m` : `${s}s`);

/**
 * A rounds block longer than 5 minutes runs as 2-3 movements. Each row is a
 * movement the user can swap; the whole mix can be collapsed to one movement.
 */
export function CardioMixCard({ cardioBlock, onSwapSegment, onKeepSingle, testId = 'cardio-mix-card' }) {
  const [collapsing, setCollapsing] = useState(false);
  const segments = cardioBlock?.rounds_segments || [];
  if (segments.length < 2) return null;

  const keepSingle = async () => {
    if (collapsing) return;
    setCollapsing(true);
    try {
      await onKeepSingle?.();
    } finally {
      setCollapsing(false);
    }
  };

  return (
    <div className="mt-4 pt-3 border-t border-white/5" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent text-accent-foreground text-[10px] font-heading font-black uppercase tracking-wider">
          <Layers size={11} strokeWidth={2.75} /> Mix · {segments.length} movements
        </span>
        <span className="text-xs font-mono text-white/40">{cardioBlock.duration_minutes} min total</span>
      </div>

      <div className="space-y-2">
        {segments.map((seg, idx) => {
          const p = seg.rounds_protocol || {};
          return (
            <motion.button
              key={`${seg.modality_id}-${idx}`}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.06, duration: 0.25 }}
              onClick={() => onSwapSegment?.(idx)}
              className="w-full flex items-center gap-3 rounded-xl bg-[#1A1A1A] border border-white/[0.07] p-2.5 text-left hover:border-accent/40 active:scale-[0.98] transition-all group"
              data-testid={`${testId}-segment-${idx}`}
            >
              <span className="w-6 h-6 shrink-0 rounded-full bg-black/60 border border-white/10 flex items-center justify-center font-heading text-xs text-accent">
                {idx + 1}
              </span>
              <div className="w-14 h-14 shrink-0 rounded-lg overflow-hidden bg-black border border-white/10">
                <CardioMedia
                  modalityId={seg.modality_id}
                  src={seg.image_url || getCardioImage(seg.modality_name, seg.modality_id)}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading text-base uppercase tracking-tight text-white leading-tight truncate">
                  {seg.modality_name}
                </div>
                <div className="text-xs font-mono text-white/45 mt-0.5">
                  {p.sets}×{workLabel(p.work_duration_seconds)} · {p.rest_duration_seconds}s rest · {p.total_duration_minutes} min
                </div>
              </div>
              <RefreshCw
                size={14}
                className="shrink-0 text-white/25 group-hover:text-accent transition-colors"
                strokeWidth={2.5}
              />
            </motion.button>
          );
        })}
      </div>

      <button
        onClick={keepSingle}
        className="mt-3 w-full flex items-center justify-center gap-2 py-2 rounded-xl border border-dashed border-white/15 text-white/40 hover:text-accent hover:border-accent/35 active:scale-[0.98] transition-all text-[11px] font-heading font-bold uppercase tracking-wider"
        data-testid={`${testId}-keep-single`}
      >
        {collapsing ? <Loader2 size={12} className="animate-spin" /> : <Minimize2 size={12} strokeWidth={2.5} />}
        Keep it single
      </button>
    </div>
  );
}
